import { zAuthFromAdminMetadata } from './chunk-2RR4A6N3.js';
import { zUserRole } from './chunk-2XXSK4OM.js';
import { zId, zDateTimeString, zNumericEnum } from './chunk-H27MX3SA.js';
import { z } from 'zod';

// ../base/types/util.ts
function objectKeysStrict(obj, treatAsNumber) {
  return Object.keys(obj).map((v) => {
    {
      return Number(v);
    }
  });
}

// src/constants/role-permissions.ts
var ROLE_PERMISSIONS = {
  account: {
    account: {
      admin: ["delete"]
    },
    apitokens: {
      admin: ["create", "delete", "edit", "view"]
    },
    billing: {
      admin: ["edit", "view"]
    },
    data_sharing: {
      admin: ["create", "delete", "edit", "view"]
    },
    drm_policies: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["view"],
      manager: ["view"],
      "read-only": ["view"]
    },
    integration: {
      admin: ["create", "delete", "edit", "view"]
    },
    properties: {
      admin: ["create", "delete", "edit", "view"]
    },
    usage: {
      admin: ["view"]
    },
    usage_entitlements: {
      admin: ["view"],
      "content-editor": ["view"],
      manager: ["view"],
      "read-only": ["view"]
    },
    users: {
      admin: ["create", "delete", "edit", "view"]
    },
    webhooks: {
      admin: ["create", "delete", "edit", "view"]
    },
    welcome_promo: {
      admin: ["view"],
      "content-editor": ["view"],
      manager: ["view"],
      "read-only": ["view"]
    }
  },
  site: {
    access_plans: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["create", "delete", "edit", "view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    ads: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    analytics: {
      admin: ["view"],
      "content-editor": ["view"],
      manager: ["view"],
      "read-only": ["view"]
    },
    app_configs: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["edit", "view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    content: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["create", "delete", "edit", "view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    custom_reports: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["create", "delete", "edit", "view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    dns_masks: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    exports: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["create", "delete", "edit", "view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    interactivity: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["create", "delete", "edit", "view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    lists: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["create", "delete", "edit", "view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    livestream: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["create", "delete", "edit", "view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    placements: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["view"],
      manager: ["create", "delete", "edit", "view"]
    },
    players: {
      admin: ["create", "delete", "edit", "view"],
      "content-editor": ["view"],
      manager: ["create", "delete", "edit", "view"],
      "read-only": ["view"]
    },
    tools: {
      admin: ["view"],
      manager: ["view"]
    },
    usage: {
      admin: ["view"],
      "content-editor": ["view"],
      manager: ["view"],
      "read-only": ["view"]
    },
    viewer_management: {
      admin: ["create", "delete", "edit", "view"],
      manager: ["create", "delete", "edit", "view"]
    }
  }
};
var PERMISSIONS_CATEGORIES = {
  account: Object.keys(ROLE_PERMISSIONS.account),
  site: Object.keys(ROLE_PERMISSIONS.site)
};

// src/api/dashboard-scope/types.ts
var MERCHANT_IDS = {
  1: "recurly",
  2: "vindicia",
  3: "other",
  4: "internal",
  5: "zuora"
};
var MERCHANT_ID_KEYS = objectKeysStrict(MERCHANT_IDS);
var PLAYER_EDITIONS = {
  1: "free",
  2: "pro",
  3: "premium",
  4: "enterprise",
  5: "ads",
  6: "trial",
  7: "unlimited",
  8: "platinum",
  9: "starter",
  10: "business",
  11: "developer"
};
var zPlayerEdition = z.nativeEnum(PLAYER_EDITIONS);
var zPermissionAction = z.union([
  z.literal("view"),
  z.literal("create"),
  z.literal("edit"),
  z.literal("delete")
]);
var zPermissionActionsArray = z.array(zPermissionAction).optional();
var zPermissionActions = (keys) => z.object(
  keys.reduce(
    (acc, key) => {
      acc[key] = zPermissionActionsArray;
      return acc;
    },
    {}
  )
);
var zSitePermissions = z.object({
  actions: zPermissionActions(PERMISSIONS_CATEGORIES.site),
  role: zUserRole
});
var zAccountPermissions = z.object({
  actions: zPermissionActions(PERMISSIONS_CATEGORIES.account),
  role: zUserRole
});
var zSite = z.object({
  analytics_token: z.string(),
  authentication: z.object({
    analytics: z.string(),
    livestream: z.string(),
    showcase: z.string()
  }),
  name: z.string(),
  permissions: zSitePermissions,
  player_keys: z.object({
    6: z.string(),
    7: z.string(),
    8: z.string(),
    android: z.string(),
    android_v3: z.string(),
    ios: z.string(),
    ios_v3: z.string()
  }),
  site_id: zId,
  sub_system_token: z.string(),
  /**
   * Legacy (long) id token of the site
   */
  token: z.string()
});
var zDashboardScope = z.object({
  account_id: z.string(),
  authentication: z.object({
    dashboard: z.string(),
    /**
     * MFA Signature, present when user opts to remember mfa permission for 2 weeks
     */
    mfa: z.string().optional()
  }),
  created_date: zDateTimeString.full,
  // auto-converted from `number`
  /**
   * Default site of the user (usually the first created site)
   */
  default_site: z.string(),
  features: z.array(z.string()),
  /**
   * Whether the account is legacy (1) or not (0)
   */
  legacy: z.number(),
  limits: z.object({
    ad_impressions: z.number(),
    plays: z.number(),
    storage: z.number(),
    streaming: z.number(),
    videos: z.number()
  }),
  payment_provider: z.object({
    merchant_id: zNumericEnum(MERCHANT_ID_KEYS),
    merchant_ref: z.string().nullable()
  }).optional(),
  permissions: zAccountPermissions,
  player_edition: zPlayerEdition,
  player_edition_id: z.number(),
  salesforce_id: z.string().nullable(),
  sites: z.array(zSite),
  state: z.union([z.literal("normal"), z.literal("suspended")]),
  subscription_expiration: zDateTimeString.full.nullable(),
  // auto-converted from `number`
  token: z.string(),
  companion: z.object({
    fromAdmin: zAuthFromAdminMetadata.optional()
  })
});

export { zDashboardScope };
//# sourceMappingURL=chunk-YHDAKXNX.js.map
//# sourceMappingURL=chunk-YHDAKXNX.js.map